import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@ab-inbev-maz/compass';
import CanvasToolbar from '../../components/canvas/CanvasToolbar';
import CanvasSidePanel from '../../components/canvas/CanvasSidePanel';
import CanvasStage from '../../components/canvas/CanvasStage';
import { CanvasEditorProvider, useCanvasEditor } from '../../components/canvas/CanvasEditorContext';
import Loading from '../../features/sso-auth/components/Loading';
import { onBeforeLocaleChange } from '../../features/sso-auth/store/authStore';
import { useCanvasAutosave } from '../../hooks/canvas/useCanvasAutosave';
import { canvasService } from '../../services/canvasService';
import { useAlertStore } from '../../stores/alertStore';
import { parseEditorState, toEditorState, type CanvasEditorConfig } from './canvasEditorState';

interface CanvasEditorLayoutProps {
  config: CanvasEditorConfig;
}

const CanvasEditorLayout = ({ config }: CanvasEditorLayoutProps) => {
  const { status, lastSavedAt, isOnline, saveNow, flush } = useCanvasAutosave({
    canvasId: config.id,
    initialSavedAt: config.modifiedAt,
  });
  const { unloadedLayerCount } = useCanvasEditor();
  const showAlert = useAlertStore((state) => state.showAlert);

  // Pending edits must reach the backend while the canvas still belongs to the current country.
  useEffect(
    () =>
      onBeforeLocaleChange(async () => {
        await flush({ withImage: true });
      }),
    [flush]
  );

  useEffect(() => {
    if (unloadedLayerCount === 0) return;
    showAlert({
      text: 'Atención',
      message:
        unloadedLayerCount === 1
          ? 'Un elemento del canvas no se pudo cargar.'
          : `${unloadedLayerCount} elementos del canvas no se pudieron cargar.`,
      status: 'warning',
    });
  }, [unloadedLayerCount, showAlert]);

  return (
    <div className="flex flex-col h-full">
      <CanvasToolbar
        title={config.name || undefined}
        canvasId={config.id}
        save={{ status, lastSavedAt, isOnline, onSave: saveNow, onFlush: flush }}
      />
      <div className="flex flex-1 overflow-hidden">
        <CanvasSidePanel />
        <CanvasStage />
      </div>
    </div>
  );
};

// Opens the saved canvas `/canvas/:id`: with the data the list passed along, or fetched (e.g. after a refresh).
const CanvasEditorPage = () => {
  const { id = '' } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  // The data the list passed is only good for opening the canvas: the browser keeps the navigation state
  // across reloads, where it would be outdated (and skip the fetch). So it's kept here and dropped from history.
  const [openedWith] = useState(() => parseEditorState(state, id));
  const stateConfig = openedWith?.id === id ? openedWith : parseEditorState(state, id);
  const [fetched, setFetched] = useState<CanvasEditorConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasStateConfig = stateConfig !== null;

  useEffect(() => {
    if (state != null) void navigate(`/canvas/${id}`, { replace: true, state: null });
  }, [state, id, navigate]);

  // The canvas belongs to the current country: after switching, the app opens the list instead.
  useEffect(() => onBeforeLocaleChange(async () => '/canvas'), []);

  useEffect(() => {
    if (hasStateConfig) return;
    let cancelled = false;
    setFetched(null);
    setError(null);
    canvasService
      .getCanvas(id)
      .then((canvas) => {
        if (!cancelled) setFetched(parseEditorState(toEditorState(canvas), canvas.id));
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error al obtener el canvas');
      });
    return () => {
      cancelled = true;
    };
  }, [id, hasStateConfig]);

  const config = stateConfig ?? fetched;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 h-full">
        <p className="text-base text-gray-700">{error}</p>
        <Button label="Volver a mis canvas" variant="primary" className="cursor-pointer" onClick={() => navigate('/canvas')} />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading message="Esto solo va tomar un momento, estamos cargando el canvas." />
      </div>
    );
  }

  return (
    // Keyed by id: another canvas gets a fresh editor (history, autosave state).
    <CanvasEditorProvider
      key={config.id}
      initialConfig={{
        width: config.width,
        height: config.height,
        sizeId: config.sizeId,
        backgroundColor: config.backgroundColor,
        structure: config.structure,
      }}
    >
      <CanvasEditorLayout config={config} />
    </CanvasEditorProvider>
  );
};

export default CanvasEditorPage;
